import { MouseEvent, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { isNull, isUndefined } from 'lodash'
import { v4 as uuid } from 'uuid'
import { Button } from '@microservices/wiskey-react-components'
import { CircularProgress, Typography } from '@mui/material'

import { getFormData } from '@components/DisplayForm/helpers'
import { VariableValue } from '@components/Variables/types'

import { useExecuteCommandMutation, useFetchActionByIdQuery } from '@redux/api'
import { openFormDialog } from '@redux/reducers/dialogWindowManager.reducer'
import { addFormHistory } from '@redux/reducers/formHistory.reducer'

import { PrintFormVouPayload, runPrintFormVouFromAction } from '@features/print-forms'
import { PrintTrigger } from '@features/print-forms/components/PrintTrigger'

import { processFieldChangeVariable, useAppDispatch, useOpenForm } from '@hooks'
import {
  executeCode,
  getActionCommandRequest,
  getCommandRequestData,
  getInitialPositionWindow,
  getValuesFromVariables,
  showSnackbarNotification,
} from '@helpers'
import { ACTION_CODE, ENTITY, FORM_TYPE } from '@constants'
import {
  ActionRow,
  DIALOG_WINDOW_TYPE,
  ExecuteCommandBody,
  FormDataEnriched,
  FormElement,
  FormRow,
  FormType,
  GETVariableDTO,
  Row,
} from '@types'

import { EVENT_NAME, publish } from '../../events'

type ActionButtonProps = {
  element: FormElement<FormDataEnriched>
  row: Row
  currentRowValues: Row
  fieldsFormConfig?: FormRow[]
  latestVariables: GETVariableDTO[]
  outerVariables: VariableValue[]
  instanceId: string
  formConfig?: FormType
}

const buildSrcObj = (
  row: Row,
  currentRowValues: Row,
  latestVariables: GETVariableDTO[],
  outerVariables: VariableValue[],
  formValues: Row
) => ({
  rowData: row,
  currentRowValues,
  formVariables: latestVariables,
  outerVariables: getValuesFromVariables(outerVariables),
  formValues,
  enrichedObject: row, // Дублирование, нужно проверить используется ли где-то такой параметр
})

type SrcObj = ReturnType<typeof buildSrcObj>
/** Контекст для executeCode: srcObj (обратная совместимость) + верхнеуровневые параметры */
const buildScriptContext = (srcObj: SrcObj, commandData?: unknown): Record<string, unknown> => ({
  srcObj: commandData !== undefined ? { commandData, ...srcObj } : srcObj,
  rowData: srcObj.rowData,
  currentRowValues: srcObj.currentRowValues,
  formValues: srcObj.formValues,
  formVariables: srcObj.formVariables,
  outerVariables: srcObj.outerVariables,
  enrichedObject: srcObj.enrichedObject,
  ...(commandData !== undefined && { commandData }),
})

export const ActionButton = ({
  element,
  row,
  currentRowValues,
  fieldsFormConfig,
  outerVariables,
  latestVariables,
  instanceId,
  formConfig,
}: ActionButtonProps) => {
  const { watch, setValue, getValues } = useFormContext()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { openForm } = useOpenForm()

  const { data: actionData, isLoading } = useFetchActionByIdQuery(element.data as number, {
    skip: !element.data,
  })

  const [executeCommand, { isLoading: isCommandLoading }] = useExecuteCommandMutation()
  const [printPayload, setPrintPayload] = useState<PrintFormVouPayload | null>(null)
  const [runtimeError, setRuntimeError] = useState<string | null>(null)

  const handleExecuteCommandAction = async (action: ActionRow, srcObj: SrcObj) => {
    const request = getActionCommandRequest({
      fieldsFormConfig,
      action,
      currentRowValues,
      scriptContext: buildScriptContext(srcObj),
    })
    if (!request) return

    await executeCommand({
      method: request.method,
      objectCode: request.objectCode,
      commandCode: request.commandCode,
      isCommandResponse: request.isCommandResponse,
      params: request.params,
      body: request.hasFile
        ? getFormData(request.body as Record<string, unknown>)
        : (request.body as ExecuteCommandBody),
      resource: request.resource,
    })
  }

  const handleChangeVariableValueAction = async (action: ActionRow, srcObj: SrcObj) => {
    const { command, variable, jsValue } = action

    let variableValue: unknown

    if (command) {
      const body =
        command.httpMethod !== 'GET'
          ? getCommandRequestData(command.body ?? [], currentRowValues, false, fieldsFormConfig, {
              ...srcObj,
            })
          : {}

      const hasFile = Object.values(body as Record<string, unknown>).some(v => v instanceof File)

      const params = getCommandRequestData(
        command.parameters ?? [],
        currentRowValues,
        undefined,
        undefined,
        { ...srcObj }
      )

      const commandData = await executeCommand({
        method: command.httpMethod,
        objectCode: command.objectCode,
        commandCode: command.name,
        isCommandResponse: Boolean(command.isCommandResponse),
        params,
        body: hasFile ? getFormData(body) : command.httpMethod !== 'GET' ? body : undefined,
      }).unwrap()

      if (jsValue) {
        const result = executeCode(jsValue, buildScriptContext(srcObj, commandData))
        variableValue = result === false ? '' : result ?? commandData
      } else {
        variableValue = commandData
      }
    } else if (jsValue) {
      const result = executeCode(jsValue, buildScriptContext(srcObj))
      variableValue = result === false ? '' : result ?? ''
    }

    publish(
      EVENT_NAME.CHANGE_VARIABLE_VALUE,
      {
        ...variable,
        entityId: variable?.entityId ?? element.data,
        value: variableValue,
        updatedOn: new Date().toISOString(),
      },
      instanceId
    )
  }

  const handlePrintFormVouAction = async (action: ActionRow, srcObj: SrcObj) => {
    const payload = await runPrintFormVouFromAction({
      executeCommand,
      action,
      currentRowValues,
      scriptContext: buildScriptContext(srcObj),
      fieldsFormConfig,
    })

    setPrintPayload(payload)
  }

  const handleOpenFormAction = (
    action: ActionRow,
    event: MouseEvent<HTMLButtonElement>,
    srcObj: SrcObj
  ) => {
    const { objectFormCode, objectCode } = action
    const id = uuid()
    const initialPosition = getInitialPositionWindow(event)

    dispatch(
      addFormHistory({
        id,
        form: {
          formCode: objectFormCode ?? '',
          objectCode: objectCode ?? '',
          event: FORM_TYPE.CREATE,
          objectId: null,
          title: '',
          id,
          isWindow: true,
        },
      })
    )

    dispatch(
      openFormDialog({
        id,
        parentDialogId: null,
        type: DIALOG_WINDOW_TYPE.FORM,
        title: null,
        meta: {
          objectCode: objectCode ?? '',
          variables: srcObj.formVariables,
          formCode: objectFormCode ?? '',
          viewCode: '',
          path: objectFormCode ?? '',
          event: FORM_TYPE.CREATE,
          globalId: id,
          entityType: ENTITY.VIEW,
          isIndependentForm: true,
          outerData: {
            commands: action.redefinitionCommands ?? [],
            srcObj: row,
          },
        },
        initialPosition,
      })
    )
  }

  const handleSetFieldValue = (action: ActionRow, srcObj: SrcObj) => {
    if (!action.jsValue) return

    const initValueField = executeCode(action.jsValue, srcObj)

    if (initValueField === false || isUndefined(initValueField) || isNull(initValueField)) return

    setValue(action.objectFieldCode, initValueField as never, {
      shouldDirty: true,
      shouldTouch: true,
    })

    // setValue не генерирует type:'change' в watch, поэтому
    // useChangeVariableWithAction не узнает об изменении — вызываем вручную
    const updatedFormValues = getValues()
    processFieldChangeVariable(updatedFormValues, action.objectFieldCode, {
      row: row ?? updatedFormValues,
      formVariables: latestVariables,
      outerVariables,
      actions: formConfig?.actions ?? [],
      uniqueEventId: instanceId,
      executeCommand,
      openForm,
    })
  }

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    if (!actionData) return

    setRuntimeError(null)
    const srcObj = buildSrcObj(row, currentRowValues, latestVariables, outerVariables, watch())

    try {
      switch (actionData.actionCode) {
        case ACTION_CODE.EXECUTE_COMMAND:
          await handleExecuteCommandAction(actionData, srcObj)
          break
        case ACTION_CODE.PRINT_FORM_VOU:
          await handlePrintFormVouAction(actionData, srcObj)
          break
        case ACTION_CODE.CHANGE_VARIABLE_VALUE:
          await handleChangeVariableValueAction(actionData, srcObj)
          break
        case ACTION_CODE.SET_FIELD_VALUE:
          handleSetFieldValue(actionData, srcObj)
          break
        case ACTION_CODE.OPEN_ANOTHER_FORM_MODEL:
        case ACTION_CODE.OPEN_FORM_MODEL:
          handleOpenFormAction(actionData, event, srcObj)
          break
        default:
          showSnackbarNotification({
            text: t('actionErrors.unsupportedActionButton', {
              actionCode: actionData.actionCode,
            }),
            options: { variant: 'warning', showCancel: true },
          })
      }
    } catch (err) {
      console.error(`[ActionButton] Error executing action "${actionData.actionCode}":`, err)
      setRuntimeError((err as Error)?.message?.trim() || t('actionErrors.genericExecutionFailed'))
    }
  }

  const handlePrintDone = () => setPrintPayload(null)

  return (
    <>
      <Button
        disabled={!actionData || isLoading || isCommandLoading}
        variant='contained'
        sx={{
          position: 'relative',
          height: 25,
          cursor: 'pointer',
          ':disabled': {
            cursor: 'default',
          },
        }}
        onClick={handleClick}
      >
        <Typography
          variant='body2'
          sx={{
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {element.title}
        </Typography>
        {isCommandLoading && (
          <CircularProgress size={14} sx={{ ml: 2, position: 'absolute', right: 8 }} />
        )}
      </Button>
      {runtimeError && (
        <Typography color='error' ml={1} variant='caption'>
          {runtimeError}
        </Typography>
      )}
      {printPayload && (
        <PrintTrigger
          config={printPayload.config}
          records={printPayload.records}
          onDone={handlePrintDone}
        />
      )}
    </>
  )
}
